import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import {
  NotificationResult,
  ToastNotification,
  ToastNotificationKind,
  ValidatorColor,
} from '@/web-components/import-wizard-v2/typings/notification';
import i18n from '@/i18n';

export function mapToastNotification(
  notification: NotificationResult,
  shape: AnnotationShape | undefined
): ToastNotification | undefined {
  if (notification.validatorName === 'commercialRemark') {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.commercial-remark')
        .toString(),
      subTitle: i18n.t(notification.message).toString(),
      caption: `${notification.printPositionName}`,
    };
  }

  if (notification.validatorName === 'emptyPrintPosition') {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.empty-print-position')
        .toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.empty-print-position-msg')
        .toString(),
      caption: `${notification.printPositionName}`,
    };
  }

  if (notification.validatorName === 'smallDetails' && shape) {
    const smallDetailsStatusTranslation = shape.smallDetailsResult
      ? getSmallDetailsStatus(shape.smallDetailsResult.status)
      : '';

    const shapeDescription =
      shape.kind === 'Logo' ? shape.fileName : shape.w2pText.text;

    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n.t('wizard-v2.notification-options.small-details').toString(),
      subTitle: i18n
        .tc('wizard-v2.notification-options.small-details-msg', 1, {
          status: smallDetailsStatusTranslation,
        })
        .toString(),
      caption: `${notification.printPositionName} : ${shapeDescription}`,
    };
  }

  if (notification.validatorName === 'outOfBoundaries' && shape) {
    const shapeDescription =
      shape.kind === 'Logo' ? shape.fileName : shape.w2pText.text;

    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.out-of-boundaries')
        .toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.out-of-boundaries-msg')
        .toString(),
      caption: `${notification.printPositionName} : ${shapeDescription}`,
    };
  }

  if (
    notification.validatorName === 'fileTypeValidator' &&
    shape &&
    shape.kind === 'Logo'
  ) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n.t('wizard-v2.notification-options.file-type').toString(),
      subTitle: i18n
        .tc('wizard-v2.notification-options.file-type-msg', 1, {
          message: notification.message,
        })
        .toString(),
      caption: `${notification.printPositionName} : ${shape.fileName}`,
    };
  }

  if (
    notification.validatorName === 'vectorFileValidator' &&
    shape &&
    shape.kind === 'Logo'
  ) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n.t('wizard-v2.notification-options.vector-file').toString(),
      subTitle: i18n
        .tc('wizard-v2.notification-options.file-type-msg', 1, {
          message: notification.message,
        })
        .toString(),
      caption: `${notification.printPositionName} : ${shape.fileName}`,
    };
  }

  if (
    notification.validatorName === 'resolutionValidator' &&
    shape &&
    shape.kind === 'Logo'
  ) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.file-resolution')
        .toString(),
      subTitle:
        notification.validatorColor === 'Red'
          ? i18n
              .tc(
                'wizard-v2.notification-options.file-resolution-error-msg',
                1,
                {
                  message: notification.message,
                }
              )
              .toString()
          : i18n
              .tc(
                'wizard-v2.notification-options.file-resolution-warning-msg',
                1,
                {
                  message: notification.message,
                }
              )
              .toString(),
      caption: `${notification.printPositionName} : ${shape.fileName}`,
    };
  }

  if (
    notification.validatorName === 'spotColorsValidator' &&
    shape &&
    shape.kind === 'Logo'
  ) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n.t('wizard-v2.notification-options.spot-colors').toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.spot-colors-msg')
        .toString(),
      caption: `${notification.printPositionName} : ${shape.fileName}`,
    };
  }

  if (notification.validatorName === 'maxNumberOfColoursValidator') {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.maximum-number-of-colors')
        .toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.maximum-number-of-colors-msg')
        .toString(),
      caption: `${notification.printPositionName}`,
    };
  }

  if (
    notification.validatorName === 'gradientFileValidator' &&
    shape &&
    shape.kind === 'Logo'
  ) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n.t('wizard-v2.notification-options.gradient-file').toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.gradient-file-msg')
        .toString(),
      caption: `${notification.printPositionName} : ${shape.fileName}`,
    };
  }

  if (notification.validatorName === 'recommendedPrintPosition' && shape) {
    return {
      kind: getToastNotificationKind(notification.validatorColor),
      title: i18n
        .t('wizard-v2.notification-options.recommended-print-position')
        .toString(),
      subTitle: i18n
        .t('wizard-v2.notification-options.recommended-print-position-msg')
        .toString(),
      caption: `${notification.printPositionName} : ${
        shape.kind === 'Logo' ? shape.fileName : ''
      }`,
    };
  }
}

export function getToastNotificationKind(
  validatorColor: ValidatorColor
): ToastNotificationKind {
  if (validatorColor === 'Red') return 'error';
  if (validatorColor === 'Orange') return 'warning';
  return 'info';
}

export function getSmallDetailsStatus(status: string): string {
  if (status === 'Average') {
    return i18n.t('wizard-v2.small-details-options.status.average').toString();
  } else if (status === 'Bad') {
    return i18n.t('wizard-v2.small-details-options.status.bad').toString();
  } else {
    return i18n.t('wizard-v2.small-details-options.status.good').toString();
  }
}
